import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, 
  TextField, Box, Typography, Select, MenuItem, FormControl, InputLabel, 
  Chip, IconButton, Divider, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'axios';

const ManageListsModal = ({ open, onClose, lists, refreshLists, gamesMap, studiosMap }) => {
  const [selectedListId, setSelectedListId] = useState('new');
  const [listName, setListName] = useState('');
  const [listDesc, setListDesc] = useState('');
  const [listType, setListType] = useState('Static');
  const [listGames, setListGames] = useState([]);
  const [listStudios, setListStudios] = useState([]);
  const [smartConfig, setSmartConfig] = useState({ TimeWindowWeeks: 4, TopGamesLimit: 10 });
  
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Load suggestions
  useEffect(() => {
    if (open) {
      fetchSuggestions();
    }
  }, [open]);

  useEffect(() => {
    if (selectedListId === 'new') {
      setListName('');
      setListDesc('');
      setListType('Static');
      setListGames([]);
      setListStudios([]);
    } else {
      const list = lists.find(l => l.ListID === selectedListId);
      if (list) {
        setListName(list.Name || '');
        setListDesc(list.Description || '');
        setListType(list.Type || 'Static');
        setListGames(list.Games || []);
        setListStudios(list.StudioIDs || []);
        setSmartConfig(list.SmartConfig || { TimeWindowWeeks: 4, TopGamesLimit: 10 });
      }
    }
  }, [selectedListId, lists]);

  const fetchSuggestions = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/suggested-additions`, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
      });
      setSuggestions(res.data.Suggestions || []);
    } catch (e) {
      console.warn("Failed to fetch suggestions", e);
    }
  };

  const handleSaveInfo = async () => {
    if (!listName.trim()) return;
    setLoading(true);
    try {
      if (selectedListId === 'new') {
        const res = await axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/lists`, 
          { Name: listName, Description: listDesc, Type: listType, SmartConfig: listType === 'SmartSuggested' ? smartConfig : undefined },
          { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
        );
        await refreshLists();
        setSelectedListId(res.data.ListID);
      } else {
        await axios.patch(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/lists/${selectedListId}`, 
          { Name: listName, Description: listDesc, Type: listType, SmartConfig: listType === 'SmartSuggested' ? smartConfig : undefined },
          { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
        );
        await refreshLists();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteList = async () => {
    if (!window.confirm("Are you sure you want to delete this list?")) return;
    setLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/lists/${selectedListId}`, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
      });
      setSelectedListId('new');
      await refreshLists();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGame = async (gameId) => {
    if (!gameId || selectedListId === 'new') return;
    if (listGames.includes(gameId)) return;
    
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/lists/${selectedListId}/games`, 
        { GameIDs: [gameId] },
        { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
      );
      await refreshLists();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGame = async (gameId) => {
    setLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/lists/${selectedListId}/games/${gameId}`, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
      });
      await refreshLists();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudio = async (studioId) => {
    if (!studioId || selectedListId === 'new') return;
    if (listStudios.includes(studioId)) return;
    
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/lists/${selectedListId}/studios`, 
        { StudioIDs: [studioId] },
        { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
      );
      await refreshLists();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStudio = async (studioId) => {
    setLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/lists/${selectedListId}/studios/${studioId}`, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
      });
      await refreshLists();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const allGamesOptions = Object.keys(gamesMap || {}).map(id => ({ id, label: gamesMap[id] }));
  const allStudiosOptions = Object.keys(studiosMap || {}).map(id => ({ id, label: studiosMap[id] }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Manage Anomaly Lists</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Select List</InputLabel>
            <Select
              value={selectedListId}
              label="Select List"
              onChange={(e) => setSelectedListId(e.target.value)}
            >
              <MenuItem value="new"><em>-- Create New List --</em></MenuItem>
              {lists.map(l => (
                <MenuItem key={l.ListID} value={l.ListID}>{l.Name} ({l.Type || 'Static'})</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography variant="h6" gutterBottom>
          {selectedListId === 'new' ? 'New List Details' : 'Edit List Details'}
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <TextField 
            label="List Name" 
            fullWidth 
            value={listName} 
            onChange={(e) => setListName(e.target.value)} 
          />
          <TextField 
            label="Description" 
            fullWidth 
            value={listDesc} 
            onChange={(e) => setListDesc(e.target.value)} 
          />
          <FormControl fullWidth>
            <InputLabel>List Type</InputLabel>
            <Select
              value={listType}
              label="List Type"
              onChange={(e) => setListType(e.target.value)}
            >
              <MenuItem value="Static">Static (Add specific Games)</MenuItem>
              <MenuItem value="SmartStudio">Smart Studio (Automatically monitors all games from Studios)</MenuItem>
              <MenuItem value="SmartSuggested">Top Corrected Games (Smart List)</MenuItem>
            </Select>
          </FormControl>
          
          {listType === 'SmartSuggested' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                label="Lookback Period (Weeks)" 
                type="number"
                fullWidth 
                value={smartConfig.TimeWindowWeeks} 
                onChange={(e) => setSmartConfig({...smartConfig, TimeWindowWeeks: e.target.value})} 
                inputProps={{ min: 1 }}
              />
              <TextField 
                label="Top Games Limit" 
                type="number"
                fullWidth 
                value={smartConfig.TopGamesLimit} 
                onChange={(e) => setSmartConfig({...smartConfig, TopGamesLimit: e.target.value})} 
                inputProps={{ min: 1 }}
              />
            </Box>
          )}
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleSaveInfo} disabled={loading || !listName.trim()}>
              {selectedListId === 'new' ? 'Create List' : 'Update Details'}
            </Button>
            {selectedListId !== 'new' && (
              <Button variant="outlined" color="error" onClick={handleDeleteList} disabled={loading}>
                Delete List
              </Button>
            )}
          </Box>
        </Box>

        {selectedListId !== 'new' && listType !== 'SmartSuggested' && (
          <>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" gutterBottom>
              {listType === 'SmartStudio' ? 'Manage Studios in List' : 'Manage Games in List'}
            </Typography>
            
            {listType === 'SmartStudio' ? (
              <Box>
                <Autocomplete
                  options={allStudiosOptions}
                  getOptionLabel={(opt) => opt.label}
                  onChange={(e, val) => { if(val) handleAddStudio(val.id); }}
                  renderInput={(params) => <TextField {...params} label="Search and Add Studio" />}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {listStudios.map(sId => (
                    <Chip 
                      key={sId} 
                      label={studiosMap[sId] || sId} 
                      onDelete={() => handleRemoveStudio(sId)} 
                      disabled={loading}
                    />
                  ))}
                  {listStudios.length === 0 && <Typography color="textSecondary">No studios added yet.</Typography>}
                </Box>
              </Box>
            ) : (
              <Box>
                <Autocomplete
                  options={allGamesOptions}
                  getOptionLabel={(opt) => opt.label}
                  onChange={(e, val) => { if(val) handleAddGame(val.id); }}
                  renderInput={(params) => <TextField {...params} label="Search and Add Game" />}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {listGames.map(gId => (
                    <Chip 
                      key={gId} 
                      label={gamesMap[gId] || gId} 
                      onDelete={() => handleRemoveGame(gId)} 
                      disabled={loading}
                    />
                  ))}
                  {listGames.length === 0 && <Typography color="textSecondary">No games added yet.</Typography>}
                </Box>
              </Box>
            )}

            {listType !== 'SmartStudio' && suggestions.length > 0 && (
              <>
                <Divider sx={{ my: 4 }} />
                <Typography variant="subtitle1" gutterBottom>
                  🔥 Recommended Additions
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  These games have had the most reassignments recently and may be good candidates for monitoring.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {suggestions.filter(s => !listGames.includes(s.GameID)).map(s => (
                    <Chip 
                      key={s.GameID}
                      label={`${gamesMap[s.GameID] || s.GameID} (${s.CorrectionCount} reassignments)`}
                      onClick={() => handleAddGame(s.GameID)}
                      color="secondary"
                      variant="outlined"
                      clickable
                      disabled={loading}
                    />
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageListsModal;
